// frontend/js/storySimulation.js - Story Simulation Entry and Session Management
import { apiBaseUrl } from './config.js';
import { t, confirmT, updateElementText } from './translationHelper.js';

/**
 * Show Story Simulation Modal - Entry point
 * Follows mementoMori pattern with theme selection
 */
export function showStorySimulationModal() {
  // First show the Catena Narrativa introduction
  showCatenaIntroduction();
}

/**
 * Show Catena Narrativa Introduction Modal
 * Explains the concept before story selection
 */
function showCatenaIntroduction() {
   const modal = document.createElement('div');
   modal.classList.add('story-modal');

   modal.innerHTML = `
   <div class="story-modal-content catena-intro">
     <h1>${t('story.catena_title', 'Catena Narrativa')}</h1>

     <div class="intro-section">
       <p class="subtitle">
         <em>${t('story.catena_subtitle', 'Story building as supply chains: trade, customize, and consolidate through three phases to complete the story creation process.')}</em>
       </p>

       <button id="continueToCatena" class="primary-btn">
         Continue to Story Selection
       </button><br>

       <h3>${t('story.process_heading', 'Process:')}</h3>
       <ul>
         <li><strong>${t('story.materials_motion_title', 'Materials in motion:')}</strong> ${t('story.materials_motion_desc', 'Trade narrative elements as commodities. Customizing them as you trade. Value forms through the exchanging process to construct a story.')}</li>
         <li><strong>${t('story.three_passes_title', 'Three passes:')}</strong> ${t('story.three_passes_desc', 'Origin (beginning) → Transit (middle) → Assembly (end) ( 3min + 3min + 2min ≈8 minutes total). Each phase ends after the time expires, or you redeem, resulting in your bullet point commodities being secured into a personal story ledger.')}</li>
         <li><strong>${t('story.composition_title', 'Composition:')}</strong> ${t('story.composition_desc', 'At the end, your ledger is transformed into a short story generated from your customized trading of assets and redeemed secured bullet points.')}</li>
       </ul>

       <p>
         <strong>${t('story.commodity_x_label', 'Commodity X')} <em>${t('story.catena_title', 'Catena Narrativa')}</em></strong>: ${t('story.commodity_x_desc', 'is part of ZEconomy where profits are measured in <strong>Life Profit</strong>— the quality and quantity of intellect, affection, partying... etc, all of life\'s divers happenings, promoting being—over accumulation.')}
       </p>

       <p class="footer-note">
         ${t('story.footer_note_consumption', 'Commodity X where Consumption is relational: you cannot consume without mutual assent. The commodity is not the object; the commodity is the interaction.')}<br><br>
         ${t('story.footer_note_redeem', 'Redeem relationally. Consume consciously.')}
       </p>
     </div>

     <button id="closeCatenaIntro" class="secondary-btn">
       Close
     </button>
   </div>
   `;
 
   document.body.appendChild(modal);

  // Continue button - shows genre selection
  document.getElementById('continueToCatena').addEventListener('click', () => {
    modal.remove();
    showGenreSelectionModal();
  });

  // Close button - returns to index
  document.getElementById('closeCatenaIntro').addEventListener('click', () => {
    modal.remove();
    window.location.href = 'index.html';
  });

  // Add hover effect to continue button
  const continueBtn = document.getElementById('continueToCatena');
  continueBtn.addEventListener('mouseenter', () => {
    continueBtn.style.transform = 'scale(1.05)';
    continueBtn.style.boxShadow = '0 0 30px rgba(102, 102, 255, 0.8)';
  });
  continueBtn.addEventListener('mouseleave', () => {
    continueBtn.style.transform = 'scale(1)';
    continueBtn.style.boxShadow = '0 0 20px rgba(102, 102, 255, 0.5)';
  });
}

/**
 * Show Genre Selection Modal (original story selection)
 */
function showGenreSelectionModal() {
  const modal = document.createElement('div');
  modal.classList.add('story-modal');

  modal.innerHTML = `
    <div class="story-modal-content">
      <h2>${t('story.simulation_title', 'Story Simulation')}</h2>
      <p class="intro-text">
        ${t('story.simulation_intro', 'Engage in a narrative exchange with automated participants across three distinct phases, crafting a unique short story through dynamic collaboration.')}
      </p>

      <div class="genre-section">
        <h3>${t('story.choose_genre', 'Choose Your Genre')}</h3>
        <div id="theme-selection" class="theme-selection">
          <button class="theme-btn" data-theme="scifi">
            ${t('story.genre_scifi', 'Science Fiction')} <span>${t('story.genre_scifi_desc', 'Futuristic visions and speculative worlds')}</span>
          </button>
          <button class="theme-btn" data-theme="romance">
            ${t('story.genre_romance', 'Romance')} <span>${t('story.genre_romance_desc', 'Emotion, intimacy, and human connection')}</span>
          </button>
          <button class="theme-btn" data-theme="mystery">
            ${t('story.genre_mystery', 'Mystery')} <span>${t('story.genre_mystery_desc', 'Secrets, investigation, and concealed motives')}</span>
          </button>
          <button class="theme-btn" data-theme="fantasy">
            ${t('story.genre_fantasy', 'Fantasy')} <span>${t('story.genre_fantasy_desc', 'Myth, imagination, and transcendence')}</span>
          </button>
          <button class="theme-btn" data-theme="horror">
            ${t('story.genre_horror', 'Horror')} <span>${t('story.genre_horror_desc', 'Fear, suspense, and the confrontation of the unknown')}</span>
          </button>
        </div>
      </div>

      <div class="footer-section">
        <p class="duration">${t('story.duration', 'Duration: 8 minutes (3 + 3 + 2 minute phases)')}</p>
        <button id="closeStoryModal" class="secondary-btn">Cancel</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Add hover effects and theme selection
  const themeButtons = modal.querySelectorAll('.theme-btn');
  themeButtons.forEach(btn => {
    btn.addEventListener('mouseenter', () => {
      btn.style.transform = 'scale(1.05)';
      btn.style.boxShadow = '0 0 20px rgba(102, 102, 255, 0.5)';
    });

    btn.addEventListener('mouseleave', () => {
      btn.style.transform = 'scale(1)';
      btn.style.boxShadow = 'none';
    });

    btn.addEventListener('click', async () => {
      const theme = btn.dataset.theme;
      modal.remove();
      await showItemSelection(theme);
    });
  });

  document.getElementById('closeStoryModal').addEventListener('click', () => {
    modal.remove();
  });
}

/**
 * Show item selection modal
 */
async function showItemSelection(theme) {
  const loadingModal = document.createElement('div');
  loadingModal.classList.add('modal');
  loadingModal.style.cssText = `
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    background: rgba(0, 0, 0, 0.95) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 10002 !important;
  `;

  loadingModal.innerHTML = `
    <div class="modal-content" style="
      background: #000;
      color: #66ffff;
      padding: 40px;
      border-radius: 15px;
      text-align: center;
      border: 2px solid #6666ff;
    ">
      <h2 style="margin-bottom: 20px;">${t('story.loading_elements', ' Loading Story Elements...')}</h2>
      <div style="
        width: 50px;
        height: 50px;
        border: 4px solid rgba(102, 102, 255, 0.3);
        border-top-color: #6666ff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 20px auto;
      "></div>
    </div>
  `;

  document.body.appendChild(loadingModal);

  try {
    // Fetch available items
    const response = await fetch(`${apiBaseUrl}/api/storysim/available-items/${theme}`);
    if (!response.ok) {
      throw new Error('Failed to fetch items');
    }

    const data = await response.json();
    loadingModal.remove();

    // Show selection modal
    showItemSelectionModal(theme, data.items);

  } catch (error) {
    loadingModal.remove();
    console.error('❌ [StorySimulation] Error fetching items:', error);
    window.alert(`${t('story.failed_load_elements', 'Failed to load story elements')}: ${error.message}`);
  }
}

/**
 * Show item selection modal with available items
 */
function showItemSelectionModal(theme, items) {
  const modal = document.createElement('div');
  modal.classList.add('modal', `item-modal-${theme}`);
  modal.style.cssText = `
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    background: rgba(0, 0, 0, 0.95) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 10002 !important;
    overflow-y: auto !important;
  `;

  const itemsHTML = items.map(item => `
    <div class="item-card" data-item-id="${item.itemId}">
      <div class="item-card-title">
        ${item.name}
      </div>
      <div class="item-card-base">
        <strong>${item.baseElement}</strong>
      </div>
      <div class="item-card-description">
        ${item.description}
      </div>
    </div>
  `).join('');

  modal.innerHTML = `
    <div class="modal-content" style="
      color: #fff;
      padding: 30px;
      border-radius: 15px;
      max-width: 600px;
      max-height: 80vh;
      overflow-y: auto;
    ">
      <h2 style="margin-bottom: 15px; font-size: 1.5em;">
        ${t('story.choose_starting_element', 'Choose Your Starting Element')}
      </h2>
      <p style="color: #ccc; margin-bottom: 20px; line-height: 1.5;">
        ${t('story.select_one_element', "Select one story element to begin with. You'll also receive 2 random elements.")}
      </p>
      <div id="items-list" style="margin-bottom: 20px;">
        ${itemsHTML}
      </div>
      <button id="skipSelection" style="
        background: rgba(255, 255, 255, 0.1);
        color: #ccc;
        border: 1px solid #666;
        padding: 12px 30px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 1em;
        width: 100%;
      ">
        Skip Selection (Get 3 Random Elements)
      </button>
    </div>
  `;

  document.body.appendChild(modal);

  // Add click handlers
  const itemCards = modal.querySelectorAll('.item-card');
  itemCards.forEach(card => {
    card.addEventListener('mouseenter', () => {
      card.style.transform = 'scale(1.02)';
    });

    card.addEventListener('mouseleave', () => {
      card.style.transform = 'scale(1)';
    });

    card.addEventListener('click', () => {
      const itemId = card.dataset.itemId;
      modal.remove();
      createStorySession(theme, itemId);
    });
  });

  document.getElementById('skipSelection').addEventListener('click', () => {
    modal.remove();
    createStorySession(theme, null);
  });
}

/**
 * Create story simulation session
 */
async function createStorySession(theme, selectedItemId = null) {
  const loadingModal = document.createElement('div');
  loadingModal.classList.add('modal');
  loadingModal.style.cssText = `
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    background: rgba(0, 0, 0, 0.95) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 10002 !important;
  `;

  loadingModal.innerHTML = `
    <div class="modal-content" style="
      background: #000;
      color: #66ffff;
      padding: 40px;
      border-radius: 15px;
      text-align: center;
      border: 2px solid #6666ff;
    ">
      <h2 style="margin-bottom: 20px;">${t('story.preparing_story', ' Preparing Your Story...')}</h2>
      <p style="color: #ccc;">${t('story.setting_up_simulation', 'Setting up {theme} simulation...').replace('{theme}', theme)}</p>
      <div style="
        width: 50px;
        height: 50px;
        border: 4px solid rgba(102, 102, 255, 0.3);
        border-top-color: #6666ff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 20px auto;
      "></div>
    </div>
  `;

  document.body.appendChild(loadingModal);

  try {
    const preferredLanguage = localStorage.getItem('preferredLanguage') || 'en';

    const response = await fetch(`${apiBaseUrl}/api/sim/create-story-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        theme,
        preferredLanguage,
        selectedItemId
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create session');
    }

    const result = await response.json();

    // Store session data AND authentication data
    localStorage.setItem('portfolioId', result.portfolioId); // CRITICAL: Store portfolioId
    localStorage.setItem('token', result.token); // CRITICAL: Store token
    localStorage.setItem('sessionId', result.sessionId);
    localStorage.setItem('simulationInstanceId', result.simulationInstanceId); // Pool ID
    localStorage.setItem('role', result.role || 'user'); // User role
    localStorage.setItem('storyTheme', result.theme);
    localStorage.setItem('storyPhase', result.currentPhase);
    localStorage.setItem('storyPhaseEndsAt', result.phaseEndsAt);
    localStorage.setItem('simulationType', 'story-simulation');
    localStorage.setItem('isSimulated', 'true');

    console.log('✅ [StorySimulation] Session created:', result);

    loadingModal.innerHTML = `
      <div class="modal-content" style="
        background: #000;
        color: #00ff00;
        padding: 40px;
        border-radius: 15px;
        text-align: center;
      ">
        <h2 style="color: #00ff00; margin-bottom: 20px;">${t('story.story_begins', ' Story Begins Now')}</h2>
        <p style="color: #ccc;">${t('story.entering_world', 'Entering {theme} world in 2 seconds...').replace('{theme}', theme)}</p>
      </div>
    `;

    setTimeout(() => {
      loadingModal.remove();
      window.location.href = 'zfloor.html';
    }, 2000);

  } catch (error) {
    loadingModal.remove();
    console.error('❌ [StorySimulation] Session creation error:', error);
    window.alert(`${t('story.failed_create_session', 'Failed to create story session')}: ${error.message}`);
  }
}

/**
 * Initialize Story Simulation UI - Called in sim.html
 * Follows mementoMori pattern
 */
export async function initializeStorySimulationUI() {
  const simulationType = localStorage.getItem('simulationType');
  console.log('📖 [initializeStorySimulationUI] Called. SimulationType:', simulationType);

  if (simulationType !== 'story-simulation') {
    console.log('⚠️ [initializeStorySimulationUI] Not story-simulation mode, exiting');
    return;
  }

  const theme = localStorage.getItem('storyTheme');
  const phase = localStorage.getItem('storyPhase');

  console.log(`📖 [initializeStorySimulationUI] Theme: ${theme}, Phase: ${phase}`);

  // Add body class for CSS-based hiding (follows mementoMori pattern)
  document.body.classList.add('story-simulation-mode');

  // Hide non-story elements in story simulation
  const elementsToHide = [
    // Emotional Debt Index and Instruments
    'emotional-state-banner',
    'emotional-debt-meter',
    'social-economy-banner',
    'social-economy-actions',
    // Tools section
    'tools-banner',
    'messageButton',
    'steal-button-container',
    'ransom-button-container',
    'community-button-container',
    // Economic triggers
    'economic-banner',
    'economic-triggers-message',
    'delete-portfolio-button-container',
    'LBOButton-button-container',
    'SubsidyButton-button-container',
    // Short positions
    'shorts-banner',
    'shorted-stocks-container',
    // ZBonds
    'zbonds-banner',
    'bond-holdings'
  ];
  elementsToHide.forEach(id => {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  });
  console.log('📖 [initializeStorySimulationUI] Hidden emotional debt elements');

  // Import and initialize bullet points display
  const { initializeStoryBulletPoints } = await import('./storyBulletPoints.js');
  await initializeStoryBulletPoints();

  // Import and initialize phase manager
  const { initializeStoryPhaseManager } = await import('./storyPhaseManager.js');
  await initializeStoryPhaseManager();

  console.log('✅ [initializeStorySimulationUI] Initialization complete');
}


/**
 * Get current phase information
 */
export async function getCurrentPhase() {
  try {
    const portfolioId = localStorage.getItem('portfolioId');
    const token = localStorage.getItem('token');

    const response = await fetch(`${apiBaseUrl}/api/storysim/current-phase/${portfolioId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      throw new Error('Failed to get phase info');
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('❌ [StorySimulation] Error getting phase:', error);
    return null;
  }
}

/**
 * Advance to next phase
 */
export async function advancePhase() {
  try {
    const portfolioId = localStorage.getItem('portfolioId');
    const token = localStorage.getItem('token');

    const response = await fetch(`${apiBaseUrl}/api/storysim/advance-phase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: JSON.stringify({ portfolioId })
    });

    if (!response.ok) {
      throw new Error('Failed to advance phase');
    }

    const data = await response.json();

    // Update localStorage
    localStorage.setItem('storyPhase', data.currentPhase);
    localStorage.setItem('storyPhaseEndsAt', data.phaseEndsAt);

    console.log(`✅ [StorySimulation] Advanced to ${data.currentPhase} phase`);
    return data;

  } catch (error) {
    console.error('❌ [StorySimulation] Error advancing phase:', error);
    return null;
  }
}

/**
 * Generate final story
 */
export async function generateFinalStory() {
  try {
    const portfolioId = localStorage.getItem('portfolioId');
    const token = localStorage.getItem('token');

    console.log('🎬 [StorySimulation] Generating final story...');

    const response = await fetch(`${apiBaseUrl}/api/storysim/generate-final`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: JSON.stringify({ portfolioId })
    });

    if (!response.ok) {
      throw new Error('Failed to generate story');
    }

    const data = await response.json();

    console.log('✅ [StorySimulation] Story generated:', data.storyId);

    // Store story ID
    localStorage.setItem('storyId', data.storyId);

    return data;

  } catch (error) {
    console.error('❌ [StorySimulation] Error generating story:', error);
    return null;
  }
}

export default {
  showStorySimulationModal,
  initializeStorySimulationUI,
  getCurrentPhase,
  advancePhase,
  generateFinalStory
};
